import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Login as LoginIcon,
  Payments as PaymentsIcon,
} from "@mui/icons-material";
import {
  Button,
  Box,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "contexts/AuthContext";

// ─── Constants ───────────────────────────────────────────────────────────────

const TEAM_NAME = "Thương Gia Tiền Số";

const ROLE_REDIRECT: Record<number, string> = {
  2: "/admin",
  1: "/premium",
  0: "/",
};

// ─── Shared sx helpers ───────────────────────────────────────────────────────

const fullScreenCenter = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #1a1a2e 0%, #29263D 50%, #1a1a2e 100%)",
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <Box sx={fullScreenCenter}>
      <CircularProgress sx={{ color: "#fbbf24" }} />
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function LoginPage() {
  const navigate = useNavigate();
  const { signInWithGoogle, loading, user, profile, role } = useAuth();

  // Redirect authenticated users to their appropriate page
  useEffect(() => {
    if (!loading && user) {
      if (profile === null || profile.role === -1) {
        navigate("/pending", { replace: true });
      } else {
        const destination = ROLE_REDIRECT[role ?? 0] ?? "/";
        navigate(destination, { replace: true });
      }
    }
  }, [loading, user, profile, role, navigate]);

  if (loading) return <LoadingScreen />;

  return (
    <Box sx={fullScreenCenter}>
      <Paper
        elevation={6}
        sx={{
          p: { xs: 4, sm: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          background: "linear-gradient(145deg, #2E2B43 0%, #252338 100%)",
          borderRadius: 4,
          width: { xs: "calc(100% - 32px)", sm: 440 },
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          border: "1px solid rgba(251, 191, 36, 0.15)",
        }}
      >
        {/* Logo Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.08))",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)", opacity: 1 },
              "50%": { transform: "scale(1.08)", opacity: 0.8 },
            },
            animation: "pulse 2.5s ease-in-out infinite",
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
              color: "#1a1a2e",
            }}
          >
            <PaymentsIcon sx={{ fontSize: 30 }} />
          </Avatar>
        </Box>

        {/* Team Name */}
        <Typography
          variant="h5"
          sx={{
            color: "#fbbf24",
            fontWeight: 700,
            textAlign: "center",
            fontSize: { xs: "1.2rem", sm: "1.45rem" },
            letterSpacing: "-0.01em",
          }}
        >
          {TEAM_NAME}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body2"
          sx={{
            color: "#b8b5cc",
            textAlign: "center",
            lineHeight: 1.7,
            maxWidth: 300,
          }}
        >
          Bộ công cụ P2P an toàn và nhanh chóng
          <br />
          Đăng nhập bằng Google để vào ứng dụng
        </Typography>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: 1,
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
          }}
        />

        {/* Google Login Button */}
        <Button
          variant="contained"
          onClick={signInWithGoogle}
          disabled={loading}
          sx={{
            width: "100%",
            px: 4,
            py: 1.5,
            background: "linear-gradient(135deg, #4285F4, #356AC3)",
            "&:hover": {
              background: "linear-gradient(135deg, #5595F5, #4285F4)",
            },
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 600,
            gap: 1.5,
          }}
        >
          <LoginIcon
            sx={{
              display: "inline-block",
              width: 22,
              height: 22,
            }}
          />
          Đăng nhập với Google
        </Button>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            color: "#666",
            textAlign: "center",
            lineHeight: 1.6,
            mt: 1,
          }}
        >
          P2P – Binance
          <br />
          All rights reserved.
        </Typography>
      </Paper>
    </Box>
  );
}